import React, { useEffect, useRef, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import Tab from '../../../components/common/Tab';
import Button from '../../../components/common/Button';
import { confirmAction, showAlert } from '../../../utils/sweetAlertUtils';
import { InvoiceBatchDTO } from '../../../services/apiClient';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useInvoiceBatchApi, useInvoiceBatchRegenerationStatus } from '../../../hooks/useInvoiceBatchApi';
import InvoiceBatchRegenerationStatus from './InvoiceBatchRegenerationStatus';
import { getApiErrorMessage } from '../../../utils/apiErrorUtils';
import { useMemberApi } from '../../../hooks/useMemberApi';
import InvoiceBatchDetailPanel from './InvoiceBatchDetailPanel';
import InvoiceBatchAuditLogPanel from './InvoiceBatchAuditLogPanel';
import { convertToUserTime } from '../../../utils/dateUtils/convertToUserTime';

const InvoiceBatchDetail: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const { getById: getBatch, regenerate } = useInvoiceBatchApi();
  const { search: searchMembers } = useMemberApi();
  const [batch, setBatch] = useState<InvoiceBatchDTO | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();
  const idPassportNumberFromUrl = new URLSearchParams(location.search).get('idPassportNumber')?.trim() ?? '';
  const [filterMemberId, setFilterMemberId] = useState<string>('');
  const [isResolvingFilterMember, setIsResolvingFilterMember] = useState(false);

  const navigate = useNavigate();

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Invoice Batches', href: '/admin/invoicebatches' },
    { label: 'Batch Detail', href: '#' },
  ];

  const fetchBatch = async () => {
    if (!batchId) return;
    try {
      const response = await getBatch(batchId);
      if (response?.data) {
        setBatch(response.data);
      }
    } catch (error) {
      console.error('Error fetching batch:', error);
    }
  };

  useEffect(() => {
    fetchBatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const { data: regenerationStatus, refetch: refetchRegenerationStatus } = useInvoiceBatchRegenerationStatus(batchId);
  const isRegenerating = !!regenerationStatus?.isActive;

  // When a run we were watching finishes, reload the batch - its new object also makes the
  // invoice panels reload, so totals and invoices update without a page refresh
  const previousRegenerationStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    const current = regenerationStatus?.status;
    const previous = previousRegenerationStatus.current;
    previousRegenerationStatus.current = current;

    if ((previous === 'Queued' || previous === 'Running') && current === 'Completed') {
      fetchBatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenerationStatus?.status]);

  useEffect(() => {
    if (!idPassportNumberFromUrl) {
      setFilterMemberId('');
      setIsResolvingFilterMember(false);
      return;
    }

    (async () => {
      setIsResolvingFilterMember(true);
      setFilterMemberId('');

      try {
        const response = await searchMembers({
          filters: {
            idPassportNumber: idPassportNumberFromUrl,
          },
          page: 1,
          pageSize: 1,
          sortBy: 'CreatedUtc',
          sortDescending: true,
        });

        const memberId = response?.data?.results?.[0]?.id ?? '';
        setFilterMemberId(memberId);
      } catch (error) {
        console.error('Error resolving member from idPassportNumber:', error);
        setFilterMemberId('');
      } finally {
        setIsResolvingFilterMember(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPassportNumberFromUrl]);

  const handleRegenerate = async () => {
    if (!batchId || isSubmitting || isRegenerating) return;

    const confirmed = await confirmAction({
      title: 'Regenerate Invoice Batch?',
      text: 'This will regenerate invoices for this batch: Finalized invoices (with Invoice Numbers) are ignored completely. Draft invoices for eligible members will have line items regenerated with current prices. Draft invoices for ineligible members will be deleted. New invoices will be created for newly eligible members.',
      icon: 'warning',
      confirmButtonText: 'Yes, regenerate',
      cancelButtonText: 'Cancel',
    });

    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      const response = await regenerate(batchId);
      if (response && response.success) {
        // Picks up the Queued status straight away, which starts the polling
        await refetchRegenerationStatus();
      }
    } catch (error: any) {
      console.error('Error regenerating batch:', error);
      await refetchRegenerationStatus();
      await showAlert({
        title: 'Error',
        text: getApiErrorMessage(error, 'Failed to regenerate invoice batch'),
        icon: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMonthName = (month: number) => {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return monthNames[month - 1] || '';
  };

  // Only hold off rendering the panels while resolving a specific member from the URL.
  const awaitingFilterMember = !!idPassportNumberFromUrl && (isResolvingFilterMember || !filterMemberId);

  const cardTitle = `Invoice Batch Detail - ${batch ? `${getMonthName(batch.month ?? 0)} ${batch.year}` : ''}`;
  const cardSubtitle = `Batch No: ${batch?.batchNo || ''} | Created: ${batch?.totalCreated || 0} | Paid: ${batch?.totalPaid || 0}`
    + (batch?.renewalEmailsSentUtc ? ` | Renewals Sent: ${convertToUserTime(batch.renewalEmailsSentUtc.toString())}` : '')
    + (batch?.netcashPushDateUtc ? ` | Pushed to Netcash: ${convertToUserTime(batch.netcashPushDateUtc.toString())}` : '')
    + (idPassportNumberFromUrl ? ` | Filter: ${idPassportNumberFromUrl}` : '');
  const cardHeaderActions = (
    <div className="flex items-center space-x-2">
      <InvoiceBatchRegenerationStatus status={regenerationStatus} />
      <Button
        type="button"
        variant="secondary"
        onClick={() => navigate('/admin/invoicebatches')}
      >
        Back to Batches
      </Button>
      <Button
        type="button"
        variant="primary"
        onClick={handleRegenerate}
        isLoading={isSubmitting}
        disabled={isSubmitting || isRegenerating}
      >
        {isRegenerating ? 'Regenerating…' : 'Regenerate'}
      </Button>
    </div>
  );

  const tabs = [
    {
      id: 'standard',
      title: 'Standard Payments',
      content: batchId ? (
        <Card title={cardTitle} subtitle={cardSubtitle} headerActions={cardHeaderActions}>
          <InvoiceBatchDetailPanel
            key="standard"
            batchId={batchId}
            batch={batch}
            isDebitOrderPayment={false}
            filterMemberId={filterMemberId}
            awaitingFilterMember={awaitingFilterMember}
          />
        </Card>
      ) : null,
    },
    {
      id: 'debitorder',
      title: 'Debit Order Payments',
      content: batchId ? (
        <Card title={cardTitle} subtitle={cardSubtitle} headerActions={cardHeaderActions}>
          <InvoiceBatchDetailPanel
            key="debitorder"
            batchId={batchId}
            batch={batch}
            isDebitOrderPayment={true}
            filterMemberId={filterMemberId}
            awaitingFilterMember={awaitingFilterMember}
          />
        </Card>
      ) : null,
    },
    {
      id: 'auditlog',
      title: 'Audit Log',
      content: batchId ? (
        <Card title={cardTitle} subtitle={cardSubtitle} headerActions={cardHeaderActions}>
          <InvoiceBatchAuditLogPanel batchId={batchId} />
        </Card>
      ) : null,
    },
  ];

  return (
    <AdminLayout
      activeMenuItem="/admin/invoicebatches"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Finance.InvoiceBatch.View"
    >
      <div className="h-full">
        <Tab tabs={tabs} defaultActiveTab="standard" />
      </div>
    </AdminLayout>
  );
};

export default InvoiceBatchDetail;
